import { CountCacheEntry } from "./protocol";

export interface CounterSyncDeps {
  myUid: () => string;
  sendCount: (id: string, value: number) => void;
  isPresent: (uid: string) => boolean;
  now: () => number;
  onOwnershipChange: () => void;
}

interface ForeignCounter {
  owner: string;
  authority: number;
  localBaseAtAuthority: number;
  display: number;
}

const RECONCILE_TAU_S = 0.35;
const SNAP_DELTA = 8;
const BROADCAST_MIN_INTERVAL_MS = 1000;

/**
 * Tracks counter ownership and the non-owner display prediction for a room
 * session. Ownership follows play, not placement: a counter becomes owned only when
 * a player semi-selects its cluster (see {@link setOwner}), and its owner's value is
 * the authority everyone else mirrors. Ownership is sticky — it survives the owner
 * moving on to another cluster — and rides its own discrete channel, so the fast
 * value channel never changes it ({@link receiveCount} is owner-passive). A counter
 * nobody has semi-selected is unowned: every client counts it independently, as in
 * solo. The owner broadcasts its value change-triggered and throttled to ~1/sec
 * (never on a loop); a non-owner runs the same collisions locally for a smooth
 * prediction and eases that toward `authority + locally observed increments` over
 * ~1s, snapping when drift is large and freezing at the last authority once the
 * owner leaves. With no room state every counter resolves to its plain local value,
 * so solo behaviour is unchanged.
 */
export class CounterSync {
  private legacy = new Set<string>();
  private mine = new Set<string>();
  private readonly foreign = new Map<string, ForeignCounter>();
  private readonly remoteCreated = new Set<string>();
  private readonly knownIds = new Set<string>();
  private readonly pendingValue = new Map<string, number>();
  private readonly lastSentValue = new Map<string, number>();
  private readonly lastSentAt = new Map<string, number>();
  private readonly lastLocal = new Map<string, number>();

  constructor(private readonly deps: CounterSyncDeps) {}

  /** Drops all room state so the next solo session resolves every counter locally. */
  reset(): void {
    this.legacy = new Set<string>();
    this.mine = new Set<string>();
    this.foreign.clear();
    this.remoteCreated.clear();
    this.knownIds.clear();
    this.pendingValue.clear();
    this.lastSentValue.clear();
    this.lastSentAt.clear();
    this.lastLocal.clear();
  }

  /**
   * Records the counters present at room start / on the snapshot as legacy and seeds
   * knownIds so the census (deletion tracking) is accurate from the first frame.
   * These start unowned — ownership is conferred only by a semi-selection — so every
   * client counts them locally until then.
   */
  markLegacy(ids: Iterable<string>): void {
    this.legacy = new Set<string>(ids);
    for (const id of this.legacy) {
      this.knownIds.add(id);
    }
  }

  /** Records a counter that arrived via a remote op, seeding knownIds so the census tracks it before the next reconcile. */
  noteRemoteCounter(id: string): void {
    if (this.mine.has(id) || this.legacy.has(id)) {
      return;
    }
    this.remoteCreated.add(id);
    this.knownIds.add(id);
  }

  /**
   * Reconciles the live counter census: tracks which count ids exist so a deletion
   * can be detected, and drops all state for a counter that is gone. Placement no
   * longer confers ownership — a counter is owned only once a player semi-selects
   * its cluster (see {@link setOwner}), so a freshly placed counter stays unowned
   * (each client counts it locally) until then. Returns whether the owned/foreign
   * set changed (so the caller can repaint outlines / the panel).
   */
  reconcile(currentCountIds: Iterable<string>): boolean {
    let changed = false;
    const live = new Set<string>(currentCountIds);
    for (const id of live) {
      this.knownIds.add(id);
    }
    for (const id of [...this.knownIds]) {
      if (live.has(id)) {
        continue;
      }
      this.knownIds.delete(id);
      this.legacy.delete(id);
      this.remoteCreated.delete(id);
      this.pendingValue.delete(id);
      this.lastSentValue.delete(id);
      this.lastSentAt.delete(id);
      if (this.mine.delete(id) || this.foreign.delete(id)) {
        changed = true;
      }
    }
    return changed;
  }

  /**
   * Applies an authoritative value for a counter owned by another client (live
   * relay or snapshot seed). The local accumulator at receive time anchors the
   * prediction so later local collisions ride on top of the new authority without
   * double counting. Ignored for a counter we own. Owner-passive: the owner of an
   * already-known foreign counter is left untouched here (ownership changes only via
   * {@link setOwner} / a snapshot seed), so a stray in-flight value from the previous
   * owner can never flip the outline color. Returns whether ownership of this counter
   * just became known (a first foreign entry).
   */
  receiveCount(id: string, owner: string, value: number): boolean {
    if (this.mine.has(id)) {
      return false;
    }
    this.legacy.delete(id);
    this.knownIds.add(id);
    const localValue = this.lastLocal.get(id) ?? 0;
    const existing = this.foreign.get(id);
    if (existing === undefined) {
      this.foreign.set(id, { owner, authority: value, localBaseAtAuthority: localValue, display: value });
      return true;
    }
    existing.authority = value;
    existing.localBaseAtAuthority = localValue;
    return false;
  }

  /**
   * Reassigns a counter's owner in response to a semi-selection (local trigger) or
   * an owner-reassign relay from another client. Taking ownership (owner is me) moves
   * the counter into the owned set so this client becomes its broadcast authority;
   * yielding it (owner is someone else) moves it to the foreign set, re-anchoring the
   * prediction on the current local accumulator so the value keeps rising smoothly.
   * Ownership is sticky: it changes only here (and on a snapshot seed), never from the
   * value channel. Returns whether ownership actually changed (so the caller repaints
   * outlines / the panel).
   */
  setOwner(id: string, owner: string): boolean {
    this.legacy.delete(id);
    this.knownIds.add(id);
    const localValue = this.lastLocal.get(id) ?? 0;
    if (owner === this.deps.myUid()) {
      if (this.mine.has(id)) {
        return false;
      }
      this.foreign.delete(id);
      this.mine.add(id);
      return true;
    }
    const wasMine = this.mine.delete(id);
    const existing = this.foreign.get(id);
    if (existing === undefined) {
      this.foreign.set(id, { owner, authority: localValue, localBaseAtAuthority: localValue, display: localValue });
      return true;
    }
    const changed = wasMine || existing.owner !== owner;
    existing.owner = owner;
    existing.localBaseAtAuthority = localValue;
    return changed;
  }

  /** Seeds the foreign-counter prediction from the snapshot's cached values, resolving each owner slot to a uid. */
  applySnapshotCounts(counts: Record<string, CountCacheEntry>, resolveOwner: (slot: number) => string | null): boolean {
    let changed = false;
    for (const [id, entry] of Object.entries(counts)) {
      const owner = resolveOwner(entry.owner);
      if (owner === null || owner === this.deps.myUid()) {
        continue;
      }
      if (this.receiveCount(id, owner, entry.value)) {
        changed = true;
      }
    }
    return changed;
  }

  /** The owner uid of a counter (mine → my uid, foreign → its owner), or null for a legacy / unknown counter. */
  ownerOf(id: string): string | null {
    if (this.mine.has(id)) {
      return this.deps.myUid();
    }
    const st = this.foreign.get(id);
    return st === null || st === undefined ? null : st.owner;
  }

  /**
   * The value to paint for a counter this frame. A counter we own or a legacy
   * counter shows its plain local value (the owner is the authority; legacy is
   * independent). A foreign counter eases its display toward `authority + locally
   * observed increments`, snapping when the gap is large (a reset / big drift) and
   * freezing at the last authority once its owner has left. Owned counters stage
   * their value for the throttled broadcast.
   */
  resolveDisplay(id: string, localValue: number, dt: number): number {
    this.lastLocal.set(id, localValue);
    const st = this.foreign.get(id);
    if (st === undefined) {
      if (this.mine.has(id)) {
        this.pendingValue.set(id, localValue);
      }
      return localValue;
    }
    if (!this.deps.isPresent(st.owner)) {
      st.display = st.authority;
      st.localBaseAtAuthority = localValue;
      return Math.round(st.authority);
    }
    const target = st.authority + Math.max(0, localValue - st.localBaseAtAuthority);
    const diff = target - st.display;
    if (Math.abs(diff) >= SNAP_DELTA) {
      st.display = target;
    } else {
      st.display += diff * (1 - Math.exp(-dt / RECONCILE_TAU_S));
    }
    return Math.round(st.display);
  }

  /**
   * Owner-side broadcast: sends each owned counter whose value changed since the
   * last send, at most once per BROADCAST_MIN_INTERVAL_MS. The first send for a
   * counter (e.g. its placement) is immediate so ownership is announced at once;
   * later changes are throttled and an unchanged value is never sent (no loop).
   */
  flush(): void {
    const now = this.deps.now();
    for (const [id, value] of this.pendingValue) {
      if (!this.mine.has(id)) {
        continue;
      }
      const lastValue = this.lastSentValue.get(id);
      if (lastValue === value) {
        continue;
      }
      const lastAt = this.lastSentAt.get(id);
      if (lastAt !== undefined && now - lastAt < BROADCAST_MIN_INTERVAL_MS) {
        continue;
      }
      this.deps.sendCount(id, value);
      this.lastSentValue.set(id, value);
      this.lastSentAt.set(id, now);
    }
  }
}
